import { toaster } from '@/components/Toaster'
import { useCreateTask } from '@/feature/execution-board/api'
import { type ObjectErrors, setFormErrors } from '@/lib/utils/forms'
import { DragEndEvent } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { zodResolver } from '@hookform/resolvers/zod'
import { HTTPError } from 'ky'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import type { LocationStop } from '../SortableLocationRow'
import { TaskLocation } from '../TaskLocationPopout'
import {
  buildErrorSummary,
  getDetailError,
  hasFieldErrors,
  mapFieldName,
} from './CreateTaskModal.errors'
import {
  CreateTaskFormData,
  CreateTaskInitialData,
  FORM_DEFAULTS,
  StopErrors,
  createTaskSchema,
} from './CreateTaskModal.types'
import {
  checkConsecutiveDuplicates,
  createEmptyStop,
  createInitialStops,
  validateStops,
} from './CreateTaskModal.utils'

interface UseCreateTaskFormOptions {
  isActive: boolean
  initialData?: CreateTaskInitialData
  onClose: () => void
  onTaskCreated?: () => void
}

export function useCreateTaskForm({
  isActive,
  initialData,
  onClose,
  onTaskCreated,
}: UseCreateTaskFormOptions) {
  const { trigger: createTask, isMutating } = useCreateTask()

  const [stops, setStops] = useState<LocationStop[]>(() => createInitialStops(initialData))
  const [stopErrors, setStopErrors] = useState<Map<string, StopErrors>>(new Map())
  const [formError, setFormError] = useState<string | null>(null)

  const formMethods = useForm<CreateTaskFormData>({
    resolver: zodResolver(createTaskSchema),
    defaultValues: FORM_DEFAULTS,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  const { handleSubmit: handleSubmitBase, reset, setError } = formMethods

  const handleSubmit = (onValid: (data: CreateTaskFormData) => void | Promise<void>) => {
    return handleSubmitBase(onValid)
  }

  useEffect(() => {
    if (isActive) {
      reset(FORM_DEFAULTS)
      setStops(createInitialStops(initialData))
      setStopErrors(new Map())
      setFormError(null)
    } else {
      reset(FORM_DEFAULTS)
      setStops(createInitialStops())
      setStopErrors(new Map())
      setFormError(null)
    }
  }, [isActive, reset, initialData])

  const stopIds = useMemo(() => stops.map((s) => s.id), [stops])

  useEffect(() => {
    setStopErrors((prev) => checkConsecutiveDuplicates(stops, prev))
  }, [stops])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    setStops((prev) => {
      const oldIndex = prev.findIndex((s) => s.id === active.id)
      const newIndex = prev.findIndex((s) => s.id === over.id)
      return arrayMove(prev, oldIndex, newIndex)
    })
    setStopErrors(new Map())
  }, [])

  const handleLocationChange = useCallback((stopId: string, location: TaskLocation) => {
    setStops((prev) =>
      prev.map((s) =>
        s.id === stopId
          ? {
              ...s,
              location,
              locationType: location.type,
              locationId: location.type === 'temporary' ? null : (location.id as number),
            }
          : s,
      ),
    )
    setStopErrors((prev) => {
      const existing = prev.get(stopId)
      if (!existing?.location) return prev
      const next = new Map(prev)
      const { location: _, ...rest } = existing
      if (Object.keys(rest).length === 0) {
        next.delete(stopId)
      } else {
        next.set(stopId, rest)
      }
      return next
    })
  }, [])

  const handleDatetimeChange = useCallback((stopId: string, datetime: string) => {
    setStops((prev) => {
      const updated = prev.map((s) => (s.id === stopId ? { ...s, datetime } : s))

      setStopErrors((prevErrors) => {
        const next = new Map(prevErrors)
        const stopIndex = updated.findIndex((s) => s.id === stopId)

        const existing = next.get(stopId)
        if (existing) {
          const { datetime: _, ...rest } = existing
          if (Object.keys(rest).length === 0) {
            next.delete(stopId)
          } else {
            next.set(stopId, rest)
          }
        }

        if (datetime) {
          const date = new Date(datetime)

          if (stopIndex > 0) {
            const prevStop = updated[stopIndex - 1]
            if (prevStop.datetime) {
              const prevDate = new Date(prevStop.datetime)
              if (date <= prevDate) {
                const current = next.get(stopId) ?? {}
                next.set(stopId, { ...current, datetime: 'Must be after the previous stop' })
              }
            }
          }

          if (stopIndex < updated.length - 1) {
            const nextStop = updated[stopIndex + 1]
            if (nextStop.datetime) {
              const nextDate = new Date(nextStop.datetime)
              const nextExisting = next.get(nextStop.id) ?? {}
              if (nextDate <= date) {
                next.set(nextStop.id, {
                  ...nextExisting,
                  datetime: 'Must be after the previous stop',
                })
              } else {
                const { datetime: _, ...rest } = nextExisting
                if (Object.keys(rest).length === 0) {
                  next.delete(nextStop.id)
                } else {
                  next.set(nextStop.id, rest)
                }
              }
            }
          }
        }

        return next
      })

      return updated
    })
  }, [])

  const handleAddStop = useCallback(() => {
    setStops((prev) => [...prev, createEmptyStop()])
  }, [])

  const handleRemoveStop = useCallback((stopId: string) => {
    setStops((prev) => prev.filter((s) => s.id !== stopId))
    setStopErrors((prev) => {
      const next = new Map(prev)
      next.delete(stopId)
      return next
    })
  }, [])

  const onSubmit = async (data: CreateTaskFormData) => {
    const errors = validateStops(stops)
    if (errors.size > 0) {
      setStopErrors(errors)
      setFormError('Please fix location errors before submitting.')
      return
    }

    try {
      setFormError(null)
      setStopErrors(new Map())

      await createTask({
        ...data,
        locations: stops.map((stop) => ({
          originalObjectId: stop.locationType !== 'temporary' ? stop.locationId : null,
          type: stop.locationType.toUpperCase(),
          name: stop.location?.name,
          address: stop.location?.address,
          geoJson: stop.location?.geoJson,
          plannedArrival: stop.datetime ? new Date(stop.datetime) : undefined,
        })),
      })

      toaster({
        type: 'success',
        message: 'Task created successfully',
      })

      reset()
      onTaskCreated?.()
      onClose()
    } catch (err) {
      let backendErrors: ObjectErrors | null = null

      if (err instanceof HTTPError) {
        try {
          backendErrors = await err.response?.json()
        } catch {
          setFormError('Failed to create task. Please try again.')
          return
        }
      }

      if (!backendErrors || typeof backendErrors !== 'object') {
        setFormError('Failed to create task. Please try again.')
        return
      }

      if (hasFieldErrors(backendErrors)) {
        setFormErrors(setError, backendErrors, mapFieldName)
        setFormError(buildErrorSummary(backendErrors))
      } else {
        setFormError(getDetailError(backendErrors))
      }
    }
  }

  const handleCancel = useCallback(() => {
    reset()
    onClose()
  }, [reset, onClose])

  return {
    formMethods,
    formError,
    isMutating,
    stops,
    stopIds,
    stopErrors,
    handleSubmit,
    handleDragEnd,
    handleLocationChange,
    handleDatetimeChange,
    handleAddStop,
    handleRemoveStop,
    handleCancel,
    onSubmit,
  }
}
